import { MATRIX_LEVELS, formatUsd } from "../../data/site";
import { useLanguage } from "../../i18n/LanguageContext";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";

export const MatrixLevels = () => {
    const { t } = useLanguage();
    const chips = [
        { v: "10%", l: "Salary Fund", c: "text-nx-text border-white/15 bg-white/[0.04]" },
        { v: "50%", l: "Affiliate", c: "text-nx-lime border-nx-lime/30 bg-nx-lime/[0.06]" },
        { v: "40%", l: "Matrix", c: "text-nx-blue border-nx-blue/40 bg-nx-blue/[0.06]" },
    ];
    return (
        <section data-testid="matrix-levels-section" className="border-t border-white/5 bg-nx-alt py-20 sm:py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <SectionHeader
                        label={t("Next Global Matrix")}
                        testId="matrix-levels"
                        title={<>{t("Twelve Levels.")}<br />{t("One-Time Payment.")}</>}
                    />
                    <Reveal delay={0.15} className="flex flex-wrap gap-2.5">
                        {chips.map((s) => (
                            <div key={s.l} className={`rounded-xl border px-4 py-3 ${s.c}`} data-testid={`distribution-${s.l.toLowerCase().replace(/\s+/g, "-")}`}>
                                <span className="block text-lg font-extrabold tabular">{s.v}</span>
                                <span className="block text-[10px] font-bold uppercase tracking-[0.14em] opacity-80">{t(s.l)}</span>
                            </div>
                        ))}
                    </Reveal>
                </div>

                <Reveal delay={0.2} className="mt-12 hidden overflow-hidden rounded-2xl border border-white/5 md:block">
                    <table className="w-full text-left rtl:text-right" data-testid="matrix-levels-table">
                        <thead>
                            <tr className="border-b border-white/5 bg-nx-elev text-[11px] font-bold uppercase tracking-[0.16em] text-nx-mute">
                                <th className="px-6 py-4 text-start">{t("Level")}</th>
                                <th className="px-6 py-4 text-end">{t("Level Price")}</th>
                                <th className="px-6 py-4 text-end">{t("10% Salary Fund")}</th>
                                <th className="px-6 py-4 text-end">{t("50% Affiliate")}</th>
                                <th className="px-6 py-4 text-end">{t("40% Matrix")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {MATRIX_LEVELS.map((r) => (
                                <tr
                                    key={r.level}
                                    className="border-b border-white/[0.04] bg-nx-alt transition-colors duration-200 last:border-0 hover:bg-nx-elev"
                                    data-testid={`matrix-level-row-${r.level}`}
                                >
                                    <td className="px-6 py-4">
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-nx-soft text-xs font-extrabold text-nx-text tabular">
                                            {String(r.level).padStart(2, "0")}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-end text-sm font-extrabold text-nx-text tabular">{formatUsd(r.price)}</td>
                                    <td className="px-6 py-4 text-end text-sm font-semibold text-nx-sub tabular">{formatUsd(r.salary)}</td>
                                    <td className="px-6 py-4 text-end text-sm font-semibold text-nx-lime tabular">{formatUsd(r.affiliate)}</td>
                                    <td className="px-6 py-4 text-end text-sm font-semibold text-nx-blue tabular">{formatUsd(r.matrix)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Reveal>

                <div className="mt-10 space-y-3 md:hidden">
                    {MATRIX_LEVELS.map((r, i) => (
                        <Reveal key={r.level} delay={Math.min(i * 0.04, 0.3)}>
                            <div className="rounded-2xl border border-white/5 bg-nx-elev p-5" data-testid={`matrix-level-card-${r.level}`}>
                                <div className="flex items-center justify-between">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-nx-soft text-xs font-extrabold text-nx-text tabular">
                                        {String(r.level).padStart(2, "0")}
                                    </span>
                                    <span className="text-lg font-extrabold text-nx-text tabular">{formatUsd(r.price)}</span>
                                </div>
                                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                                    <div className="rounded-lg bg-nx-soft py-2">
                                        <span className="block text-[9px] font-bold uppercase tracking-[0.12em] text-nx-mute">{t("Salary 10%")}</span>
                                        <span className="block text-xs font-bold text-nx-sub tabular">{formatUsd(r.salary)}</span>
                                    </div>
                                    <div className="rounded-lg bg-nx-soft py-2">
                                        <span className="block text-[9px] font-bold uppercase tracking-[0.12em] text-nx-mute">{t("Affiliate 50%")}</span>
                                        <span className="block text-xs font-bold text-nx-lime tabular">{formatUsd(r.affiliate)}</span>
                                    </div>
                                    <div className="rounded-lg bg-nx-soft py-2">
                                        <span className="block text-[9px] font-bold uppercase tracking-[0.12em] text-nx-mute">{t("Matrix 40%")}</span>
                                        <span className="block text-xs font-bold text-nx-blue tabular">{formatUsd(r.matrix)}</span>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};
